import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

// import { useAnalytics } from '@restworld/utils-common';
import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

import { useJobPositionId } from '../../../hooks';
// import { jobPositionApiDataAtom } from '../../job-position/job-position.atoms';
import {
  doesSizeExceedsAtom,
  isCVUploadProcessCompletedAtom,
  spontaneousApplyDataAtom,
  uploadCVLoadingAtom,
  uploadFilesAtom,
  workerEntryPointErrorsAtom,
  workerIdAtom,
} from '../worker-entry-point.atoms';
import { CV_UPLOAD_NETWORK_FAILURE_CODE, isUploadNetworkFailure } from '../cv-upload-failure';

export const useEntryPoint = () => {
  const jobPositionId = useJobPositionId();
  // const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const setSizeExceeds = useSetAtom(doesSizeExceedsAtom);
  const uploadCVLoadingToggler = useSetAtom(uploadCVLoadingAtom);
  const setCVUploadedProcessComplete = useSetAtom(isCVUploadProcessCompletedAtom);
  const setWorkerId = useSetAtom(workerIdAtom);
  const setSpontaneousApplyData = useSetAtom(spontaneousApplyDataAtom);
  const uploadFiles = useAtomValue(uploadFilesAtom);
  const setErrors = useSetAtom(workerEntryPointErrorsAtom);
  const { initializeAuthentication } = useAuth();

  const workerService = useWorkersService();
  // const { fbTrackWorkerStartsApplication } = useAnalytics();

  const onUploadFileChange = useCallback(async () => {
    if (uploadFiles?.[0]) {
      if (uploadFiles[0].size / 1000000 > 20) {
        setSizeExceeds(true);
        return;
      }
      // fbTrackWorkerStartsApplication();
      try {
        // A retry must not keep showing the reason the previous attempt failed.
        setErrors({});
        uploadCVLoadingToggler(true);
        const response = await workerService.uploadCV({
          cv: uploadFiles[0],
          workerId: '',
          jobPositionId: jobPositionId,
        });
        setWorkerId(response?.data?.user_id);
        setCVUploadedProcessComplete(true);
        setTimeout(() => {
          setSpontaneousApplyData((p) => ({
            ...p,
            showCoverLetter: true,
          }));
        }, 2000);
        // No page reload here, deliberately. One was added on 2025-07-14 "refresh page after
        // lead user uploads cv", and two days later a guard on `isAuthenticated` was added to
        // narrow it. `isAuthenticated` is captured in this closure and read after the session
        // has just been established, so it always holds the value from before the upload: the
        // reload never fired for the lead it was written for, and fired only for a worker who
        // was already signed in. Fourteen months of nobody missing it says the lead does not
        // need it, and the worker who was already signed in was getting their page pulled out
        // from under them a second after uploading a CV.
        if (response?.data?.jwt) {
          await initializeAuthentication(response.data.jwt);
        }
      } catch (err: any) {
        if (isUploadNetworkFailure(err)) setErrors({ error_code: CV_UPLOAD_NETWORK_FAILURE_CODE });
        if (err?.['message']?.includes('500')) setErrors({ ...err?.data, statusCode: 500 });
        if (err?.['data']) setErrors({ ...err?.data, statusCode: err.code });
        setCVUploadedProcessComplete(false);
        uploadCVLoadingToggler(false);
        if (err?.code === 413) setSizeExceeds(true);
        return err;
      }
    }
  }, [
    uploadFiles,
    setSizeExceeds,
    uploadCVLoadingToggler,
    workerService,
    jobPositionId,
    setWorkerId,
    setCVUploadedProcessComplete,
    setSpontaneousApplyData,
    initializeAuthentication,
    setErrors,
  ]);
  return {
    onUploadFileChange,
  };
};
